/**
 * KeyItemData.ts
 * Key item definitions for quest items that are stored in a bitmask
 * Each key item has a unique bit position (ID 100+ maps to bit position 0+)
 */

export interface KeyItemDefinition {
  id: number;          // ID (100+), bit position = id - 100
  name: string;
  description: string;
}

/**
 * Key items - stored as a bitmask in gameState.keyItems
 * The bit position is calculated as: (id - 100) % 32
 * For IDs 100-131, they occupy bits 0-31
 */
export const KEY_ITEMS: Record<number, KeyItemDefinition> = {
  // Main story key items
  100: {
    id: 100,
    name: 'Crystal',
    description: 'A mysterious glowing crystal',
  },
  101: {
    id: 101,
    name: 'Crown',
    description: 'The crown needed to prove your worth',
  },
  102: {
    id: 102,
    name: 'Star Ruby',
    description: 'Opens the way to the Earth Cave depths',
  },
  103: {
    id: 103,
    name: 'Fire Crystal',
    description: 'Restores the power of fire',
  },
  104: {
    id: 104,
    name: 'Levistone',
    description: 'Makes heavy things float - needed for the airship',
  },
  105: {
    id: 105,
    name: 'Water Crystal',
    description: 'Restores the power of water',
  },
  106: {
    id: 106,
    name: 'Adamant',
    description: 'The hardest metal - can forge legendary weapons',
  },
  107: {
    id: 107,
    name: 'Wind Crystal',
    description: 'Restores the power of wind',
  },

  // Additional quest items
  108: {
    id: 108,
    name: 'Lute',
    description: 'Princess Sara\'s lute - a symbol of Cornelia',
  },
  109: {
    id: 109,
    name: 'Ship Key',
    description: 'Unlocks the pirate ship in Pravoka harbor',
  },
  110: {
    id: 110,
    name: 'Nitro Powder',
    description: 'Explosive powder - handle with care!',
  },
  111: {
    id: 111,
    name: 'Canal Key',
    description: 'Opens the canal to the outer seas',
  },
  112: {
    id: 112,
    name: 'Oxyale',
    description: 'Allows breathing underwater',
  },
  113: {
    id: 113,
    name: 'Canoe',
    description: 'Allows travel on rivers',
  },
  114: {
    id: 114,
    name: 'Floater',
    description: 'Raises the airship from the desert',
  },
  115: {
    id: 115,
    name: 'Rosetta Stone',
    description: 'Deciphers the Lufenian language',
  },
  116: {
    id: 116,
    name: 'Rat Tail',
    description: 'Proof of courage from the Castle of Ordeals',
  },
  117: {
    id: 117,
    name: 'Earth Crystal',
    description: 'Restores the power of earth',
  },
  118: {
    id: 118,
    name: 'Herb',
    description: 'A healing herb for the sick elf prince',
  },
  119: {
    id: 119,
    name: 'Mystic Key',
    description: 'Opens all locked doors',
  },
  120: {
    id: 120,
    name: 'TNT',
    description: 'Powerful explosive for clearing rubble',
  },
  121: {
    id: 121,
    name: 'Ruby',
    description: 'A precious gem for the Titan',
  },
  122: {
    id: 122,
    name: 'Rod',
    description: 'A magical rod that parts the sea',
  },
  123: {
    id: 123,
    name: 'Chime',
    description: 'Opens the way to the Mirage Tower',
  },
  124: {
    id: 124,
    name: 'Cube',
    description: 'Warps between the Mirage Tower and Sky Castle',
  },
  125: {
    id: 125,
    name: 'Bottle',
    description: 'Contains the fairy who knows about Oxyale',
  },
};

/**
 * Get key item by ID
 */
export function getKeyItem(itemId: number): KeyItemDefinition | null {
  return KEY_ITEMS[itemId] || null;
}

/**
 * Get all key items owned from a bitmask
 * @param keyItemsMask - The bitmask of owned key items from gameState
 * @returns Array of owned key item definitions
 */
export function getOwnedKeyItems(keyItemsMask: number): KeyItemDefinition[] {
  const owned: KeyItemDefinition[] = [];

  for (const [idStr, keyItem] of Object.entries(KEY_ITEMS)) {
    const id = parseInt(idStr);
    const bitPosition = (id - 100) % 32;

    if ((keyItemsMask & (1 << bitPosition)) !== 0) {
      owned.push(keyItem);
    }
  }

  return owned;
}

/**
 * Check if a specific key item is owned
 * @param keyItemsMask - The bitmask of owned key items
 * @param itemId - The key item ID to check
 * @returns true if the key item is owned
 */
export function hasKeyItem(keyItemsMask: number, itemId: number): boolean {
  const bitPosition = (itemId - 100) % 32;
  return (keyItemsMask & (1 << bitPosition)) !== 0;
}

/**
 * Add a key item to the bitmask
 * @param keyItemsMask - Current bitmask
 * @param itemId - Key item ID to add
 * @returns New bitmask with the item added
 */
export function addKeyItem(keyItemsMask: number, itemId: number): number {
  const bitPosition = (itemId - 100) % 32;
  return keyItemsMask | (1 << bitPosition);
}
